import { useReducer, useEffect, useCallback } from "react";
import { useCacheContext, CacheKeys } from "../Cache";
import {
  compareReducer,
  DEFAULT_COMPARER_STATE,
  ACTION_KEYS,
} from "./Compare.reducer";
import { getEnvs, getFilterItems, filterEnvData } from "./Compare.helpers";
import { Api } from "../../api";

export const useCompare = () => {
  const [state, dispatch] = useReducer(compareReducer, DEFAULT_COMPARER_STATE);
  const { getFromCache } = useCacheContext();

  const setEnvs = useCallback(async () => {
    const envs = getFromCache(CacheKeys.environments);

    dispatch({ type: ACTION_KEYS.SET_ENVS, payload: getEnvs(envs) });
  }, [getFromCache]);

  useEffect(() => {
    setEnvs();
    // eslint-disable-next-line
  }, []);

  const {
    envs,
    pickedEnvs: { firstEnv, secondEnv },
    envsData: { firstEnvData, secondEnvData, error, isLoading },
    compareData,
    filterItems,
    compareKey,
    isComparing,
  } = state;

  const disableCompareButton =
    firstEnv === "" || secondEnv === "" || compareKey === "" || isComparing;
  const showComparison = error === "" && isComparing;

  const handleCompareSuccess = (
    firstEnvData: unknown,
    secondEnvData: unknown
  ) => {
    const filterItems = getFilterItems(
      firstEnvData as Record<string, { id: string }>,
      compareKey
    );

    const firstEnvCompareData = filterEnvData(
      firstEnvData,
      filterItems[0].value,
      compareKey
    );

    const secondEnvCompareData = filterEnvData(
      secondEnvData,
      filterItems[0].value,
      compareKey
    );

    dispatch({
      type: ACTION_KEYS.SET_ENVS_DATA_FETCH_SUCCESS,
      payload: {
        firstEnvData,
        secondEnvData,
        firstEnvCompareData,
        secondEnvCompareData,
        filterItems,
      },
    });
  };

  const handleCompareError = () =>
    dispatch({ type: ACTION_KEYS.SET_ENVS_DATA_FETCH_ERROR });

  const onFirstEnvClick = (env: string) => {
    dispatch({ type: ACTION_KEYS.SET_FIRST_PICKED_ENV, payload: env });
  };

  const onSecondEnvClick = (env: string) => {
    dispatch({ type: ACTION_KEYS.SET_SECOND_PICKED_ENV, payload: env });
  };

  const onFilterChange = (filterKey: string) => {
    const firstEnvFilterData = filterEnvData(
      firstEnvData,
      filterKey,
      compareKey
    );

    const secondEnvFilterData = filterEnvData(
      secondEnvData,
      filterKey,
      compareKey
    );

    dispatch({
      type: ACTION_KEYS.SET_COMPARE_DATA,
      payload: {
        firstEnvData: firstEnvFilterData,
        secondEnvData: secondEnvFilterData,
      },
    });
  };

  const onComparisonKeyClick = (comparisonKey: string) => {
    dispatch({ type: ACTION_KEYS.SET_COMPARE_KEY, payload: comparisonKey });
  };

  const onCompareClick = async () => {
    window.scrollTo(0, document.body.scrollHeight);

    const firstEnvUrl = envs[firstEnv];
    const secondEnvUrl = envs[secondEnv];

    dispatch({ type: ACTION_KEYS.SET_ENVS_DATA_START_FETCH });

    try {
      const firstEnvData = await Api.getEnvData(firstEnvUrl, compareKey);
      const secondEnvData = await Api.getEnvData(secondEnvUrl, compareKey);

      handleCompareSuccess(firstEnvData, secondEnvData);
    } catch (e) {
      handleCompareError();
    }
  };

  return {
    envs: Object.keys(envs),
    disableCompareButton,
    isComparing,
    firstEnv,
    secondEnv,
    compareData,
    showComparison,
    filterItems,
    compareKey,
    error,
    isLoading,
    onComparisonKeyClick,
    onFirstEnvClick,
    onSecondEnvClick,
    onFilterChange,
    onCompareClick,
  } as const;
};
